import React, { useCallback, useEffect, useState } from 'react';

import {
  enrichSongsWithAddedByName,
  fetchUserDisplayNames,
} from './spotifyUsers';

const CLIENT_ID = process.env.REACT_APP_SPOTIFY_CLIENT_ID;

// Spotify rejects "localhost" — only loopback IPs like 127.0.0.1 are allowed over HTTP.
const normalizeRedirectUri = (uri) => {
  const trimmed = uri?.trim();
  if (!trimmed) return trimmed;

  try {
    const url = new URL(trimmed);
    if (url.hostname === 'localhost') {
      url.hostname = '127.0.0.1';
    }
    return url.toString();
  } catch {
    return trimmed.replace(/\/\/localhost\b/i, '//127.0.0.1');
  }
};

const REDIRECT_URI = normalizeRedirectUri(
  process.env.REACT_APP_SPOTIFY_REDIRECT_URI
);
const AUTH_ENDPOINT = 'https://accounts.spotify.com/authorize';
const TOKEN_ENDPOINT = 'https://accounts.spotify.com/api/token';
const SCOPES = [
  'playlist-read-private',
  'playlist-read-collaborative',
].join(' ');

const generateRandomString = (length) => {
  const possible =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return values.reduce((acc, x) => acc + possible[x % possible.length], '');
};

const sha256 = async (plain) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest('SHA-256', data);
};

const base64UrlEncode = (input) =>
  btoa(String.fromCharCode(...new Uint8Array(input)))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

const generateCodeChallenge = async (codeVerifier) => {
  const hashed = await sha256(codeVerifier);
  return base64UrlEncode(hashed);
};

const storeTokens = ({ access_token, refresh_token, expires_in }) => {
  localStorage.setItem('spotifyToken', access_token);
  if (refresh_token) {
    localStorage.setItem('spotifyRefreshToken', refresh_token);
  }
  localStorage.setItem(
    'spotifyTokenExpiry',
    String(Date.now() + expires_in * 1000)
  );
};

const clearTokens = () => {
  localStorage.removeItem('spotifyToken');
  localStorage.removeItem('spotifyRefreshToken');
  localStorage.removeItem('spotifyTokenExpiry');
};

const SpotifyAuth = ({ onPlaylistsFetched, onSongsFetched }) => {
  const [token, setToken] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPlaylists = useCallback(
    async (accessToken) => {
      try {
        const response = await fetch(
          'https://api.spotify.com/v1/me/playlists?limit=50',
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch playlists (${response.status})`);
        }

        const data = await response.json();
        const filteredPlaylists = data.items.filter((playlist) =>
          playlist.name.toLowerCase().includes('collab')
        );
        onPlaylistsFetched(filteredPlaylists);

        const allSongs = [];

        for (const playlist of filteredPlaylists) {
          let offset = 0;
          const limit = 100;
          let totalTracks = 0;

          do {
            const playlistTracksResponse = await fetch(
              `https://api.spotify.com/v1/playlists/${playlist.id}/tracks?limit=${limit}&offset=${offset}`,
              {
                headers: { Authorization: `Bearer ${accessToken}` },
              }
            );

            const tracksData = await playlistTracksResponse.json();

            if (offset === 0) {
              totalTracks = tracksData.total;
            }

            const tracksWithPlaylistId = tracksData.items.map((track) => ({
              ...track,
              playlistId: playlist.id,
              playlistName: playlist.name,
            }));

            allSongs.push(...tracksWithPlaylistId);
            offset += limit;
          } while (offset < totalTracks);
        }

        const userIds = allSongs.map((song) => song.added_by?.id);
        const displayNames = await fetchUserDisplayNames(accessToken, userIds);
        onSongsFetched(enrichSongsWithAddedByName(allSongs, displayNames));
      } catch (error) {
        console.error('Error fetching playlists', error);
      }
    },
    [onPlaylistsFetched, onSongsFetched]
  );

  const refreshAccessToken = useCallback(async () => {
    const refreshToken = localStorage.getItem('spotifyRefreshToken');
    if (!refreshToken) {
      clearTokens();
      setToken(null);
      return null;
    }

    const response = await fetch(TOKEN_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        client_id: CLIENT_ID,
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      clearTokens();
      setToken(null);
      return null;
    }

    const data = await response.json();
    storeTokens(data);
    setToken(data.access_token);
    return data.access_token;
  }, []);

  const getValidToken = useCallback(async () => {
    const storedToken = localStorage.getItem('spotifyToken');
    const expiry = Number(localStorage.getItem('spotifyTokenExpiry') || 0);

    if (!storedToken) {
      return null;
    }

    if (Date.now() >= expiry - 60_000) {
      return refreshAccessToken();
    }

    return storedToken;
  }, [refreshAccessToken]);

  const exchangeCodeForToken = useCallback(async (code, codeVerifier) => {
    const response = await fetch(TOKEN_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        client_id: CLIENT_ID,
        grant_type: 'authorization_code',
        code,
        redirect_uri: REDIRECT_URI,
        code_verifier: codeVerifier,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error_description || 'Token exchange failed');
    }

    return response.json();
  }, []);

  const handleLogin = async () => {
    setAuthError(null);

    const codeVerifier = generateRandomString(64);
    const state = generateRandomString(16);
    sessionStorage.setItem('spotify_code_verifier', codeVerifier);
    sessionStorage.setItem('spotify_auth_state', state);

    const codeChallenge = await generateCodeChallenge(codeVerifier);

    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      response_type: 'code',
      redirect_uri: REDIRECT_URI,
      scope: SCOPES,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      state,
    });

    window.location.href = `${AUTH_ENDPOINT}?${params.toString()}`;
  };

  useEffect(() => {
    const initAuth = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const returnedState = params.get('state');
      const error = params.get('error');

      if (error) {
        setAuthError(`Spotify login failed: ${error}`);
        window.history.replaceState({}, document.title, window.location.pathname);
        setIsLoading(false);
        return;
      }

      if (code) {
        const storedState = sessionStorage.getItem('spotify_auth_state');
        const codeVerifier = sessionStorage.getItem('spotify_code_verifier');

        sessionStorage.removeItem('spotify_auth_state');
        sessionStorage.removeItem('spotify_code_verifier');
        window.history.replaceState({}, document.title, window.location.pathname);

        if (!codeVerifier || returnedState !== storedState) {
          setAuthError('Login session expired. Please try again.');
          setIsLoading(false);
          return;
        }

        try {
          const tokenData = await exchangeCodeForToken(code, codeVerifier);
          storeTokens(tokenData);
          setToken(tokenData.access_token);
          await fetchPlaylists(tokenData.access_token);
        } catch (err) {
          setAuthError(err.message);
        }

        setIsLoading(false);
        return;
      }

      const validToken = await getValidToken();
      if (validToken) {
        setToken(validToken);
      }
      setIsLoading(false);
    };

    initAuth();
  }, [exchangeCodeForToken, fetchPlaylists, getValidToken]);

  const logout = () => {
    setToken(null);
    setAuthError(null);
    clearTokens();
  };

  if (isLoading) {
    return <div className="spotify-auth-container">Loading...</div>;
  }

  return (
    <div>
      {authError && (
        <p className="auth-error" style={{ color: '#ff6b6b', marginBottom: 8 }}>
          {authError}
        </p>
      )}
      {!token ? (
        <button className="btn" onClick={handleLogin}>
          Login to Spotify
        </button>
      ) : (
        <div className="spotify-auth-container">
          <button className="btn" onClick={logout}>
            Logout
          </button>
          <button className="btn" onClick={() => fetchPlaylists(token)}>
            Fetch Playlists
          </button>
        </div>
      )}
    </div>
  );
};

export default SpotifyAuth;
